/**
 * Permission utilities for tenant roles (Company Admin / Employee).
 *
 * - **Company Admin** — full access within their company; bypasses company-scoped CRUD checks.
 * - **Employee** — no implicit access; each resource requires explicit CRUD permissions.
 *
 * System Admin has no god-mode bypass in the tenant app (redirected to system frontend).
 * Prefer `useUserPermissionAccess()` in components instead of repeating role checks.
 * Pass `companyAdminBypass: false` only where Company Admins must follow custom rules
 * (e.g. profile-owner tab edit restrictions).
 */

/**
 * Canonical permission key constants.
 * Each value corresponds to the uniquePermissionKey stored in the Permission model.
 * All keys below are company-scoped resources assigned to Employees.
 */
export const PERMISSION_KEYS = {
	AI: 'aiPermissionKey',
	AI_FEATURES: 'aiFeaturesPermissionKey',
	AUTOMATIONS: 'automationsPermissionKey',
	BANK_DETAILS: 'bankDetailsPermissionKey',
	CAMPAIGNS: 'campaignsPermissionKey',
	CLIENTS: 'clientsPermissionKey',
	COMPANY_SETTINGS: 'companySettingsPermissionKey',
	COMPANY_BRANDING_SETTINGS: 'companyBrandingSettingsPermissionKey',
	DOMAINS_AND_SENDERS_SETTINGS: 'domainsAndSendersSettingsPermissionKey',
	CONTACTS: 'contactsPermissionKey',
	CRM: 'crmPermissionKey',
	EMPLOYEES: 'employeesPermissionKey',
	FINANCIAL_REPORTS: 'financialReportsPermissionKey',
	FOLDERS: 'foldersPermissionKey',
	INVOICE_SETTINGS: 'invoiceSettingsPermissionKey',
	INVOICES: 'invoicesPermissionKey',
	LEADS: 'leadsPermissionKey',
	LISTS: 'listsPermissionKey',
	PROJECTS: 'projectsPermissionKey',
	ROLES_AND_DEPARTMENTS: 'rolesDepartmentsPermissionKey',
	PROJECT_SETTINGS: 'projectSettingsPermissionKey',
	SENDERS: 'sendersPermissionKey',
	SETTINGS: 'settingsPermissionKey',
	HTML_TEMPLATES: 'htmlTemplatesPermissionKey',
	TEMPLATE_BUILDER: 'templateBuilderPermissionKey',
	TODOS_AND_TASKS: 'todosTasksPermissionKey',
	USER_PERMISSIONS: 'userPermissionsPermissionKey',
	WEBSITES: 'websitesPermissionKey',
	SUBSCRIPTION_MANAGEMENT: 'subscriptionManagementPermissionKey',
	BUY_PACKS_AND_ADDONS: 'purchaseCampaignPacksAddonsPermissionKey',
};

/** Canonical role strings stored on the user record. */
export const USER_ROLES = {
	SYSTEM_ADMIN: 'System Admin',
	COMPANY_ADMIN: 'Company Admin',
	EMPLOYEE: 'Employee',
};

export const isSystemAdminRole = (userRole) => userRole === USER_ROLES.SYSTEM_ADMIN;
export const isCompanyAdminRole = (userRole) => userRole === USER_ROLES.COMPANY_ADMIN;
export const isEmployeeRole = (userRole) => userRole === USER_ROLES.EMPLOYEE;

/** All granular permission keys are company-scoped CRUD resources. */
const COMPANY_PERMISSION_KEYS = new Set(Object.values(PERMISSION_KEYS));

/**
 * True when the section is a company resource (not app-wide System Admin UI).
 * Legacy permissionName strings (e.g. 'Contacts') are treated as company-scoped.
 */
export const isCompanyScopedPermission = (section) => {
	if (!section) return true;
	if (COMPANY_PERMISSION_KEYS.has(section)) return true;
	// Legacy permissionName values used before uniquePermissionKey migration
	return typeof section === 'string';
};

/**
 * @typedef {Object} PermissionAccessOptions
 * @property {boolean} [isCompanyAdmin]
 * @property {boolean} [isSystemAdmin] Kept for redirect detection; does not grant access
 * @property {boolean} [isEmployee]
 * @property {string} [userRole]
 * @property {boolean} [companyAdminBypass=true] Set false where Company Admin must follow custom rules
 */

/**
 * @param {PermissionAccessOptions} [options]
 */
export const normalizeAccessOptions = (options = {}) => {
	const userRole = options.userRole;
	return {
		isSystemAdmin: options.isSystemAdmin ?? isSystemAdminRole(userRole),
		isCompanyAdmin: options.isCompanyAdmin ?? isCompanyAdminRole(userRole),
		isEmployee: options.isEmployee ?? isEmployeeRole(userRole),
		userRole,
		companyAdminBypass: options.companyAdminBypass !== false,
	};
};

/**
 * Role-based bypass before checking Employee CRUD permissions.
 * - Company Admin → company-scoped sections only
 * - Employee / System Admin (tenant) → never
 */
export const hasElevatedRoleAccess = (options = {}, section = null) => {
	const ctx = normalizeAccessOptions(options);

	if (
		ctx.companyAdminBypass &&
		ctx.isCompanyAdmin &&
		isCompanyScopedPermission(section)
	) {
		return true;
	}

	return false;
};

/**
 * Bound permission helpers for a user context.
 * @param {Array} permissions
 * @param {PermissionAccessOptions} [accessOptions]
 */
export const createPermissionChecker = (permissions, accessOptions = {}) => ({
	can: (section, actions, requireAll = true) =>
		hasPermission(permissions, section, actions, requireAll, accessOptions),
	canAny: (section, actions) =>
		hasAnyPermission(permissions, section, actions, accessOptions),
});

/**
 * Check if a user has specific permissions for a given section.
 *
 * When `accessOptions` is provided:
 * - Company Admin bypasses company-scoped checks.
 * - Employee always requires matching CRUD on `permissions`.
 *
 * Without `accessOptions`, only the Employee CRUD array is evaluated (legacy behaviour).
 *
 * @param {Array}   permissions
 * @param {String}  section     uniquePermissionKey or legacy permissionName
 * @param {Array}   actions       e.g. ['create', 'read']
 * @param {Boolean} requireAll
 * @param {PermissionAccessOptions|null} [accessOptions]
 */
export const hasPermission = (
	permissions,
	section,
	actions,
	requireAll = true,
	accessOptions = null,
) => {
	if (accessOptions && hasElevatedRoleAccess(accessOptions, section)) {
		return true;
	}

	if (!Array.isArray(permissions)) return false;

	const matchingPermission = permissions.find((perm) => {
		const uniqueKey =
			perm.permission?.uniquePermissionKey || perm.uniquePermissionKey;
		if (uniqueKey && uniqueKey === section) return true;

		const permissionName = perm.permission?.permissionName || perm.permissionName;
		return permissionName === section;
	});

	if (!matchingPermission || !matchingPermission.actions) return false;

	const globalActions = matchingPermission.permission?.actions;

	const userHasAction = (action) => {
		if (globalActions && globalActions[action] !== true) return false;
		return matchingPermission.actions[action] === true;
	};

	if (requireAll) {
		return actions.every(userHasAction);
	}

	return actions.some(userHasAction);
};

/**
 * @param {PermissionAccessOptions|null} [accessOptions]
 */
export const hasAnyPermission = (
	permissions,
	section,
	actions,
	accessOptions = null,
) => {
	return hasPermission(permissions, section, actions, false, accessOptions);
};

const USER_PERMISSIONS_SECTIONS = [
	PERMISSION_KEYS.USER_PERMISSIONS,
	'User Permissions',
];

const matchesPermissionSection = (perm, sections) => {
	const uniqueKey =
		perm.permission?.uniquePermissionKey || perm.uniquePermissionKey;
	const permissionName =
		perm.permission?.permissionName || perm.permissionName;
	return sections.some(
		(section) => uniqueKey === section || permissionName === section,
	);
};

/** True when the user has update on the User Permissions section (Employee role). */
export const hasUserPermissionsUpdate = (permissions) => {
	if (!Array.isArray(permissions)) return false;
	return permissions.some(
		(perm) =>
			matchesPermissionSection(perm, USER_PERMISSIONS_SECTIONS) &&
			perm.actions?.update === true,
	);
};

/**
 * Who may assign CRUD permissions to Employees.
 * Company Admin always can; Employees need User Permissions update.
 */
export const canManageUserPermissionsTab = (permissions, userRole) =>
	isCompanyAdminRole(userRole) || hasUserPermissionsUpdate(permissions);

export const CRUD_ACTIONS = ['create', 'read', 'update', 'delete'];

/**
 * Permission groups kept in sync when assigning user CRUD (Contacts ↔ Lists).
 */
export const LINKED_PERMISSION_KEY_GROUPS = [
	[PERMISSION_KEYS.CONTACTS, PERMISSION_KEYS.LISTS],
];

/**
 * One-way inheritance: source → targets (Lists → Folders only).
 * Folders changes never flow back; Folders can diverge after Lists is set.
 */
export const ONE_WAY_PERMISSION_INHERITANCE = [
	{ from: PERMISSION_KEYS.LISTS, to: [PERMISSION_KEYS.FOLDERS] },
];

/**
 * Mirror only specific CRUD actions one-way (e.g. Campaigns create → related modules).
 */
export const ACTION_SPECIFIC_ONE_WAY_INHERITANCE = [
	{
		from: PERMISSION_KEYS.CAMPAIGNS,
		to: [
			PERMISSION_KEYS.LISTS,
			PERMISSION_KEYS.HTML_TEMPLATES,
			PERMISSION_KEYS.TEMPLATE_BUILDER,
			PERMISSION_KEYS.FOLDERS,
		],
		actions: ['create'],
	},
];

/** Granular settings modules that roll up into PERMISSION_KEYS.SETTINGS. */
export const SETTINGS_SECTION_CHILD_KEYS = [
	PERMISSION_KEYS.COMPANY_SETTINGS,
	PERMISSION_KEYS.INVOICE_SETTINGS,
	PERMISSION_KEYS.COMPANY_BRANDING_SETTINGS,
	PERMISSION_KEYS.DOMAINS_AND_SENDERS_SETTINGS,
	PERMISSION_KEYS.PROJECT_SETTINGS,
	PERMISSION_KEYS.ROLES_AND_DEPARTMENTS,
];

export const isSettingsSectionChildKey = (uniquePermissionKey) =>
	SETTINGS_SECTION_CHILD_KEYS.includes(uniquePermissionKey);

export const getDerivedPermissionTargets = (uniquePermissionKey) => {
	const targets = [];

	const group = LINKED_PERMISSION_KEY_GROUPS.find((keys) =>
		keys.includes(uniquePermissionKey),
	);
	if (group) {
		targets.push(...group.filter((key) => key !== uniquePermissionKey));
	}

	const oneWay = ONE_WAY_PERMISSION_INHERITANCE.find(
		(rule) => rule.from === uniquePermissionKey,
	);
	if (oneWay) {
		targets.push(...oneWay.to);
	}

	return [...new Set(targets)];
};

/** @deprecated Use getDerivedPermissionTargets */
export const getLinkedPermissionKeys = getDerivedPermissionTargets;

const emptyCrudActions = () => ({
	create: false,
	read: false,
	update: false,
	delete: false,
});

/**
 * SETTINGS parent permission reflects any enabled action across settings section children.
 */
export const syncSettingsParentPermission = (
	draft,
	globalPermByName,
	globalPermByKey,
) => {
	const settingsPerm = globalPermByKey?.[PERMISSION_KEYS.SETTINGS];
	if (!settingsPerm?.permissionName) return draft;

	const settingsEnabled = getEnabledGlobalActions(settingsPerm);
	const aggregated = emptyCrudActions();

	CRUD_ACTIONS.forEach((action) => {
		if (!settingsEnabled.includes(action)) return;
		aggregated[action] = SETTINGS_SECTION_CHILD_KEYS.some((childKey) => {
			const childPerm = globalPermByKey?.[childKey];
			if (!childPerm?.permissionName) return false;
			const childEnabled = getEnabledGlobalActions(childPerm);
			if (!childEnabled.includes(action)) return false;
			return draft?.[childPerm.permissionName]?.[action] === true;
		});
	});

	return {
		...draft,
		[settingsPerm.permissionName]: aggregated,
	};
};

const syncActionSpecificInheritance = (
	draft,
	sourcePerm,
	sourceActions,
	globalPermByKey,
) => {
	const rules = ACTION_SPECIFIC_ONE_WAY_INHERITANCE.filter(
		(rule) => rule.from === sourcePerm.uniquePermissionKey,
	);
	if (!rules.length) return draft;

	let updated = { ...draft };
	rules.forEach((rule) => {
		rule.actions.forEach((action) => {
			const sourceValue = Boolean(sourceActions[action]);
			rule.to.forEach((targetKey) => {
				const targetPerm = globalPermByKey?.[targetKey];
				if (!targetPerm?.permissionName) return;
				const targetEnabled = getEnabledGlobalActions(targetPerm);
				if (!targetEnabled.includes(action)) return;

				updated[targetPerm.permissionName] = {
					...(updated[targetPerm.permissionName] || emptyCrudActions()),
					[action]: sourceValue,
				};
			});
		});
	});
	return updated;
};

/**
 * Mirror CRUD actions from one module onto derived modules
 * (bidirectional Contacts ↔ Lists, one-way Lists → Folders).
 * Respects globally enabled actions on each target permission definition.
 */
export const syncLinkedUserPermissions = (
	draft,
	sourcePermissionName,
	globalPermByName,
	globalPermByKey,
) => {
	const sourcePerm = globalPermByName?.[sourcePermissionName];
	const sourceActions = draft?.[sourcePermissionName];
	if (!sourcePerm?.uniquePermissionKey || !sourceActions) return draft;

	let updated = { ...draft };
	getDerivedPermissionTargets(sourcePerm.uniquePermissionKey).forEach((linkedKey) => {
		const linkedPerm = globalPermByKey?.[linkedKey];
		if (!linkedPerm?.permissionName) return;

		const enabledActions = getEnabledGlobalActions(linkedPerm);
		const mirrored = emptyCrudActions();
		CRUD_ACTIONS.forEach((action) => {
			mirrored[action] =
				enabledActions.includes(action) && Boolean(sourceActions[action]);
		});
		updated[linkedPerm.permissionName] = mirrored;
	});

	updated = syncActionSpecificInheritance(
		updated,
		sourcePerm,
		sourceActions,
		globalPermByKey,
	);

	if (isSettingsSectionChildKey(sourcePerm.uniquePermissionKey)) {
		return syncSettingsParentPermission(updated, globalPermByName, globalPermByKey);
	}

	return updated;
};

/** CRUD actions enabled on a global Permission definition (system admin config). */
export const getEnabledGlobalActions = (permission) =>
	CRUD_ACTIONS.filter((action) => permission?.actions?.[action] === true);

/** True when the global Permission definition allows this action to be assigned. */
export const isGlobalActionEnabled = (permission, action) =>
	permission?.actions?.[action] === true;

const ACTION_LABELS = {
	create: 'create',
	read: 'view',
	update: 'edit',
	delete: 'delete',
};

/**
 * CRUD actions the current user is missing for a section.
 * Empty for Company Admin when elevated bypass applies (no restrictions).
 */
export const getMissingPermissionActions = (
	permissions,
	section,
	actions = CRUD_ACTIONS,
	accessOptions = null,
) => {
	if (accessOptions && hasElevatedRoleAccess(accessOptions, section)) {
		return [];
	}

	return actions.filter(
		(action) =>
			!hasPermission(permissions, section, [action], true, accessOptions),
	);
};

/** True when the user is missing one or more actions for a section. */
export const hasPermissionRestrictions = (
	permissions,
	section,
	actions = CRUD_ACTIONS,
	accessOptions = null,
) => getMissingPermissionActions(permissions, section, actions, accessOptions).length > 0;

/**
 * Human-readable summary for permission restriction alerts.
 * @param {Array<{ subject: string, missing: string[] }>} restrictions
 */
export const buildPermissionRestrictionMessage = (restrictions) => {
	if (!restrictions.length) return '';

	const parts = restrictions.map(({ subject, missing }) => {
		const labels = missing.map((action) => ACTION_LABELS[action] || action);
		if (missing.includes('read')) {
			return `${subject} (cannot ${labels.join(', ')})`;
		}
		return `${subject} (cannot ${labels.join(', ')})`;
	});

	if (restrictions.some(({ missing }) => missing.includes('read'))) {
		return `Your access is limited: ${parts.join('; ')}. Some data may be hidden or masked. Contact your administrator if you need broader access.`;
	}

	return `Your access is limited: ${parts.join('; ')}. Contact your administrator if you need broader access.`;
};
